import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { useTranslation } from "react-i18next";

const filterM2PositionsSchema = z.object({
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  strategy_from_id_equals: z.string().optional(),
  strategy_to_id_equals: z.string().optional(),
  strategy_from_name_contains: z.string().optional(),
  strategy_to_name_contains: z.string().optional(),
  strategy_to_isin_contains: z.string().optional(),
  strategy_to_code_contains: z.string().optional(),
  cic_code_contains: z.string().optional(),
  pledged_asset_equals: z.string().optional(),
});

export type FilterM2PositionsSchema = z.infer<typeof filterM2PositionsSchema>;

export function FilterM2Positions() {
  const { t, i18n } = useTranslation("tables");
  const isEnglish = i18n.language?.startsWith("en");
  const fields: Field[] = [
    {
      name: "date",
      type: "dateRange",
      label: t("m2_positions.searchbox.date", { defaultValue: "Date" }),
      from: { name: "date_from" },
      to: { name: "date_to" },
      col_span: 2,
    },
    {
      name: "strategy_from_id_equals",
      label: t("m2_positions.searchbox.strategy_from_id_equals", { defaultValue: "Strategy from ID" }),
      type: "input",
      input_type: "number",
    },
    {
      name: "strategy_from_name_contains",
      label: t("m2_positions.searchbox.strategy_from_name_contains", { defaultValue: "Strategy from name" }),
      type: "input",
    },
    {
      name: "strategy_to_id_equals",
      label: t("m2_positions.searchbox.strategy_to_id_equals", { defaultValue: "Strategy to ID" }),
      type: "input",
      input_type: "number",
    },
    {
      name: "strategy_to_name_contains",
      label: t("m2_positions.searchbox.strategy_to_name_contains", { defaultValue: "Strategy to name" }),
      type: "input",
    },
    {
      name: "strategy_to_isin_contains",
      label: t("m2_positions.searchbox.strategy_to_isin_contains", { defaultValue: "Strategy to ISIN" }),
      type: "input",
    },
    {
      name: "strategy_to_code_contains",
      label: t("m2_positions.searchbox.strategy_to_code_contains", { defaultValue: "Strategy to code" }),
      type: "input",
    },
    {
      name: "cic_code_contains",
      label: t("m2_positions.searchbox.cic_code_contains", { defaultValue: "CIC code" }),
      type: "input",
    },
    {
      name: "pledged_asset_equals",
      label: t("m2_positions.searchbox.pledged_asset_equals", { defaultValue: "Pledged asset" }),
      type: "select",
      useTranslation: false,
      options: [
        { value: "true", label: isEnglish ? "Yes" : "Oui" },
        { value: "false", label: isEnglish ? "No" : "Non" },
      ],
    },
  ];
  return (
    <FilterTable
      schema={filterM2PositionsSchema}
      client={Clients.M2}
      tableName={Tables.M2_POSITIONS}
      fields={fields}
      columns={2}
    />
  );
}
